import React from 'react';
import { ResponsiveSankey } from '@nivo/sankey';

interface ImprovedSankeyDiagramProps {
  data: {
    grossSalary: number;
    incomeTax: number;
    socialSecurity: number;
    netIncome: number;
    livingCosts: {
      rent: number;
      food: number;
      transport: number;
      utilities: number;
      entertainment: number;
      healthcare: number;
    };
    savings: number;
  };
  darkMode: boolean;
}

const ImprovedSankeyDiagram: React.FC<ImprovedSankeyDiagramProps> = ({ data, darkMode }) => {
  // Transform data into Nivo Sankey format
  const sankeyData = {
    nodes: [
      { id: 'gross-salary', nodeColor: '#4a90e2' },
      { id: 'income-tax', nodeColor: '#e74c3c' },
      { id: 'social-security', nodeColor: '#e67e22' },
      { id: 'net-income', nodeColor: '#2ecc71' },
      { id: 'rent', nodeColor: '#9b59b6' },
      { id: 'food', nodeColor: '#f39c12' },
      { id: 'transport', nodeColor: '#1abc9c' },
      { id: 'utilities', nodeColor: '#34495e' },
      { id: 'entertainment', nodeColor: '#e91e63' },
      { id: 'healthcare', nodeColor: '#3498db' },
      { id: 'savings', nodeColor: '#27ae60' }
    ],
    links: [
      {
        source: 'gross-salary',
        target: 'income-tax',
        value: data.incomeTax
      },
      {
        source: 'gross-salary',
        target: 'social-security',
        value: data.socialSecurity
      },
      {
        source: 'gross-salary',
        target: 'net-income',
        value: data.netIncome
      },
      {
        source: 'net-income',
        target: 'rent',
        value: data.livingCosts.rent
      },
      {
        source: 'net-income',
        target: 'food',
        value: data.livingCosts.food
      },
      {
        source: 'net-income',
        target: 'transport',
        value: data.livingCosts.transport
      },
      {
        source: 'net-income',
        target: 'utilities',
        value: data.livingCosts.utilities
      },
      {
        source: 'net-income',
        target: 'entertainment',
        value: data.livingCosts.entertainment
      },
      {
        source: 'net-income',
        target: 'healthcare',
        value: data.livingCosts.healthcare
      },
      {
        source: 'net-income',
        target: 'savings',
        value: Math.max(0, data.savings)
      }
    ].filter(link => link.value > 0)
  };

  // Node label formatter with values
  const nodeLabel = (node: any) => {
    const labels: { [key: string]: string } = {
      'gross-salary': 'Gross Salary',
      'income-tax': 'Income Tax',
      'social-security': 'Social Security',
      'net-income': 'Net Income',
      'rent': 'Rent',
      'food': 'Food',
      'transport': 'Transport',
      'utilities': 'Utilities',
      'entertainment': 'Entertainment',
      'healthcare': 'Healthcare',
      'savings': 'Savings'
    };
    
    const baseLabel = labels[node.id] || node.id;
    
    // Hide label for gross-salary node
    if (node.id === 'gross-salary') {
      return '';
    }
    
    // Add value and percentage for tax nodes (percentage of gross salary)
    if (['income-tax', 'social-security'].includes(node.id)) {
      const percentage = ((node.value / data.grossSalary) * 100).toFixed(0);
      return `${baseLabel}: $${node.value.toLocaleString()} (${percentage}%)`;
    }
    
    // Add value and percentage for expense nodes (percentage of net income)
    if (['rent', 'food', 'transport', 'utilities', 'entertainment', 'healthcare', 'savings'].includes(node.id)) {
      const percentage = ((node.value / data.netIncome) * 100).toFixed(0);
      return `${baseLabel}: $${node.value.toLocaleString()} (${percentage}%)`;
    }
    
    // For net income, just show the value
    if (node.id === 'net-income') {
      return `${baseLabel}: $${node.value.toLocaleString()}`;
    }
    
    return baseLabel;
  };

  // Value formatter
  const formatValue = (value: number) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toLocaleString()}`;
  };

  return (
    <div style={{ height: '500px', width: '100%' }}>
      <ResponsiveSankey
        data={sankeyData}
        margin={{ top: 40, right: 250, bottom: 40, left: 80 }}
        align="justify"
        colors={{ scheme: 'category10' }}
        nodeOpacity={1}
        nodeHoverOthersOpacity={0.35}
        nodeThickness={40}
        nodeSpacing={40}
        nodeBorderWidth={0}
        nodeBorderColor={{
          from: 'color',
          modifiers: [['darker', 0.8]]
        }}
        nodeBorderRadius={3}
        linkOpacity={0.5}
        linkHoverOthersOpacity={0.1}
        linkContract={3}
        enableLinkGradient={true}
        label={nodeLabel}
        labelPosition="outside"
        labelOrientation="horizontal"
        labelPadding={16}
        labelTextColor={darkMode ? '#ffffff' : '#333333'}
        legends={[]}
        theme={{
          background: 'transparent',
          text: {
            fill: darkMode ? '#ffffff' : '#333333',
            fontSize: 14,
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold'
          },
          tooltip: {
            container: {
              background: darkMode ? '#2a2a2a' : '#ffffff',
              color: darkMode ? '#ffffff' : '#333333',
              fontSize: 12,
              borderRadius: 4,
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)'
            }
          }
        }}
        nodeTooltip={({ node }) => (
          <div
            style={{
              background: darkMode ? '#2a2a2a' : '#ffffff',
              color: darkMode ? '#ffffff' : '#333333',
              padding: '9px 12px',
              border: `1px solid ${darkMode ? '#444' : '#ccc'}`,
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            <strong>{nodeLabel(node)}</strong>
            <br />
            {formatValue(node.value)}
          </div>
        )}
        linkTooltip={({ link }) => (
          <div
            style={{
              background: darkMode ? '#2a2a2a' : '#ffffff',
              color: darkMode ? '#ffffff' : '#333333',
              padding: '9px 12px',
              border: `1px solid ${darkMode ? '#444' : '#ccc'}`,
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            <strong>{nodeLabel(link.source)} → {nodeLabel(link.target)}</strong>
            <br />
            {formatValue(link.value)}
          </div>
        )}
      />
    </div>
  );
};

export default ImprovedSankeyDiagram;